import { Sun, Moon, CloudSun, CloudMoon, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning } from 'lucide-react';

// Silueta aproximada de Tenerife [lon, lat], sentido horario desde Punta de Anaga.
export const TENERIFE_OUTLINE = [
  [-16.12, 28.577], [-16.17, 28.586], [-16.22, 28.573], [-16.28, 28.562], [-16.33, 28.576],
  [-16.36, 28.546], [-16.41, 28.516], [-16.45, 28.49], [-16.50, 28.432], [-16.55, 28.42],
  [-16.60, 28.406], [-16.65, 28.396], [-16.72, 28.386], [-16.77, 28.376], [-16.82, 28.37],
  [-16.87, 28.376], [-16.92, 28.346], [-16.89, 28.30], [-16.845, 28.25], [-16.83, 28.20],
  [-16.82, 28.17], [-16.79, 28.13], [-16.76, 28.09], [-16.735, 28.06], [-16.72, 28.045],
  [-16.69, 28.0], [-16.66, 28.005], [-16.60, 28.02], [-16.55, 28.03], [-16.535, 28.045],
  [-16.49, 28.09], [-16.44, 28.15], [-16.40, 28.22], [-16.37, 28.30], [-16.37, 28.355],
  [-16.32, 28.40], [-16.24, 28.46], [-16.19, 28.505], [-16.15, 28.525],
];

export const TEIDE = { lat: 28.2723, lon: -16.6424 };
export const VIEW = { w: 1000, h: 550 };

const LON_MIN = -16.95, LON_MAX = -16.09, LAT_MIN = 27.98, LAT_MAX = 28.60;
const KM_LON = 98, KM_LAT = 111;
const PAD = 40;
const islandW = (LON_MAX - LON_MIN) * KM_LON;
const islandH = (LAT_MAX - LAT_MIN) * KM_LAT;
const SCALE = Math.min((VIEW.w - PAD * 2) / islandW, (VIEW.h - PAD * 2) / islandH);
const OFF_X = (VIEW.w - islandW * SCALE) / 2;
const OFF_Y = (VIEW.h - islandH * SCALE) / 2;

export function project(lat, lon) {
  return {
    x: OFF_X + (lon - LON_MIN) * KM_LON * SCALE,
    y: OFF_Y + (LAT_MAX - lat) * KM_LAT * SCALE,
  };
}

// Catmull-Rom → cubic Bézier closed path
export function smoothPath(points) {
  const n = points.length;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < n; i++) {
    const p0 = points[(i - 1 + n) % n], p1 = points[i], p2 = points[(i + 1) % n], p3 = points[(i + 2) % n];
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d + ' Z';
}

export const ISLAND_PATH = smoothPath(TENERIFE_OUTLINE.map(([lon, lat]) => project(lat, lon)));

export function conePath(cx, cy, heading, fov, r) {
  const a1 = ((heading - fov / 2) * Math.PI) / 180, a2 = ((heading + fov / 2) * Math.PI) / 180;
  const x1 = cx + r * Math.sin(a1), y1 = cy - r * Math.cos(a1);
  const x2 = cx + r * Math.sin(a2), y2 = cy - r * Math.cos(a2);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${fov > 180 ? 1 : 0} 1 ${x2} ${y2} Z`;
}

export function distanceKm(lat1, lon1, lat2, lon2) {
  const R = 6371, dLat = ((lat2 - lat1) * Math.PI) / 180, dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function camName(cam, language) {
  return cam?.[`name_${language}`] || cam?.name_es || '';
}

const CODE_LABELS = {
  es: { 0: 'Despejado', 1: 'Mayormente despejado', 2: 'Parcialmente nublado', 3: 'Nublado', 45: 'Niebla', 48: 'Niebla', 51: 'Llovizna', 53: 'Llovizna', 55: 'Llovizna', 56: 'Llovizna helada', 57: 'Llovizna helada', 61: 'Lluvia ligera', 63: 'Lluvia', 65: 'Lluvia fuerte', 66: 'Lluvia helada', 67: 'Lluvia helada', 71: 'Nieve ligera', 73: 'Nieve', 75: 'Nieve fuerte', 77: 'Granizo fino', 80: 'Chubascos', 81: 'Chubascos', 82: 'Chubascos fuertes', 85: 'Nieve', 86: 'Nieve', 95: 'Tormenta', 96: 'Tormenta con granizo', 99: 'Tormenta con granizo' },
  en: { 0: 'Clear', 1: 'Mostly clear', 2: 'Partly cloudy', 3: 'Overcast', 45: 'Fog', 48: 'Fog', 51: 'Drizzle', 53: 'Drizzle', 55: 'Drizzle', 56: 'Freezing drizzle', 57: 'Freezing drizzle', 61: 'Light rain', 63: 'Rain', 65: 'Heavy rain', 66: 'Freezing rain', 67: 'Freezing rain', 71: 'Light snow', 73: 'Snow', 75: 'Heavy snow', 77: 'Snow grains', 80: 'Showers', 81: 'Showers', 82: 'Heavy showers', 85: 'Snow showers', 86: 'Snow showers', 95: 'Thunderstorm', 96: 'Thunderstorm with hail', 99: 'Thunderstorm with hail' },
  de: { 0: 'Klar', 1: 'Überwiegend klar', 2: 'Teilweise bewölkt', 3: 'Bedeckt', 45: 'Nebel', 48: 'Nebel', 51: 'Nieselregen', 53: 'Nieselregen', 55: 'Nieselregen', 56: 'Gefrierender Niesel', 57: 'Gefrierender Niesel', 61: 'Leichter Regen', 63: 'Regen', 65: 'Starker Regen', 66: 'Gefrierender Regen', 67: 'Gefrierender Regen', 71: 'Leichter Schnee', 73: 'Schnee', 75: 'Starker Schnee', 77: 'Schneegriesel', 85: 'Schneeschauer', 86: 'Schneeschauer', 80: 'Schauer', 81: 'Schauer', 82: 'Starke Schauer', 95: 'Gewitter', 96: 'Gewitter mit Hagel', 99: 'Gewitter mit Hagel' },
};

export function codeLabel(code, language) {
  return (CODE_LABELS[language] || CODE_LABELS.es)[code] || CODE_LABELS.es[code] || '';
}

export function codeIcon(code, isDay = 1) {
  if (code === 0) return isDay ? Sun : Moon;
  if (code <= 2) return isDay ? CloudSun : CloudMoon;
  if (code === 3) return Cloud;
  if (code <= 48) return CloudFog;
  if (code <= 57) return CloudDrizzle;
  if (code <= 67 || (code >= 80 && code <= 82)) return CloudRain;
  if (code <= 77 || code === 85 || code === 86) return CloudSnow;
  return CloudLightning;
}

const DIRS = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
const DIRS_EN = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
export function windDirLabel(deg, language) {
  const i = Math.round(((deg % 360) + 360) % 360 / 45) % 8;
  return (language === 'es' ? DIRS : DIRS_EN)[i];
}

export const SEA_LEVELS = {
  calm: { color: '#2F9E6B', es: 'Ideal para el baño', en: 'Great for swimming', de: 'Ideal zum Baden' },
  moderate: { color: '#D69E2E', es: 'Baño con precaución', en: 'Swim with care', de: 'Baden mit Vorsicht' },
  surf: { color: '#2B6CB0', es: 'Olas para surf', en: 'Surf waves', de: 'Wellen zum Surfen' },
  rough: { color: '#C53030', es: 'Mar fuerte, evitar baño', en: 'Rough sea, avoid swimming', de: 'Raue See, nicht baden' },
};

export const SWIM_LEVELS = {
  calm: { color: '#2F9E6B', rank: 0, es: 'Ideal para el baño', en: 'Great for swimming', de: 'Ideal zum Baden' },
  ok: { color: '#3B8F6E', rank: 1, es: 'Bien para el baño', en: 'Good for swimming', de: 'Gut zum Baden' },
  caution: { color: '#9C5B0A', rank: 2, es: 'Precaución', en: 'Caution', de: 'Vorsicht' },
  rough: { color: '#C53030', rank: 3, es: 'Mar revuelto, evitar baño', en: 'Rough sea, avoid swimming', de: 'Raue See, nicht baden' },
};
const SWIM_REASONS = { waves: { es: 'oleaje', en: 'waves', de: 'Wellengang' }, wind: { es: 'viento', en: 'wind', de: 'Wind' } };
export function swimLabel(spot, language) {
  const lv = SWIM_LEVELS[spot.swim_level];
  if (!lv) return seaLabel(spot.level_sea, language);
  const base = lv[language] || lv.es;
  if (spot.swim_level === 'caution' && spot.swim_reason) return `${base}: ${SWIM_REASONS[spot.swim_reason][language] || SWIM_REASONS[spot.swim_reason].es}`;
  return base;
}

export function seaLevel(waveHeight, wavePeriod, wind) {
  if (waveHeight == null) return null;
  if (waveHeight > 2.5) return 'rough';
  if (waveHeight >= 1.0 && (wavePeriod || 0) >= 7) return 'surf';
  if (waveHeight < 0.6 && (wind == null || wind < 35)) return 'calm';
  return 'moderate';
}

export function seaLabel(level, language) {
  return SEA_LEVELS[level]?.[language] || SEA_LEVELS[level]?.es || '';
}

export function uvLabel(uv, language) {
  const l = { es: ['Bajo', 'Moderado', 'Alto', 'Muy alto', 'Extremo'], en: ['Low', 'Moderate', 'High', 'Very high', 'Extreme'], de: ['Niedrig', 'Mäßig', 'Hoch', 'Sehr hoch', 'Extrem'] }[language] || [];
  const idx = uv < 3 ? 0 : uv < 6 ? 1 : uv < 8 ? 2 : uv < 11 ? 3 : 4;
  return l[idx];
}
